import { SampleSearchKind, type SampleSearchCriteria } from '@/api/ApiClient';
import { isInhouseUser } from '@/lib/auth/isInhouseUser';
import type { SampleListMode } from '@/lib/sampleList/sampleListMode';
import { defaultFilterCriteria } from '@/stores/sampleFilterStore';

/** Sentinel `projectId` for the Samples "No Project" filter. Invalid with Snapshot kind. */
export const SAMPLE_SEARCH_NO_PROJECT = '__NO_PROJECT__';

/** Extra-filter options. User-facing copy avoids “live” / “snapshot”. */
export type SampleSearchKindOption = {
  value: SampleSearchKind;
  label: string;
  /** Shown on the dropdown item and when the option is selected. */
  tooltip: string;
};

export const SAMPLE_SEARCH_KIND_OPTIONS: SampleSearchKindOption[] = [
  {
    value: SampleSearchKind.Both,
    label: 'Current and published',
    tooltip:
      'One row per sample label: the current sample when you can access it, otherwise a published copy from a data package you can access.',
  },
  {
    value: SampleSearchKind.Active,
    label: 'Current samples',
    tooltip: 'Only samples in the current inventory. Published copies in data packages are not included.',
  },
  {
    value: SampleSearchKind.Snapshot,
    label: 'Published copies',
    tooltip:
      'Only published copies from data packages. Current inventory samples are not included, even if you also have access to them.',
  },
];

/**
 * Sets search Kind. Snapshot cannot combine with live-only filters (shipping, no-project, pending/submitted).
 * Status and reporting category are live-inventory fields; they are cleared for published-copy search.
 */
export function applySampleSearchKind(filters: SampleSearchCriteria, kind: SampleSearchKind): SampleSearchCriteria {
  const next: SampleSearchCriteria = { ...filters, kind };
  if (kind !== SampleSearchKind.Snapshot) return next;
  next.shipped = '';
  next.shippedAfter = undefined;
  next.shippedBefore = undefined;
  next.showShippable = false;
  next.onlyPending = false;
  next.onlySubmitted = false;
  next.onlyPreparedMaterials = false;
  next.status = '';
  next.reportingCategory = '';
  if (next.projectId === SAMPLE_SEARCH_NO_PROJECT) {
    next.projectId = '';
  }
  return next;
}

export type SampleSearchKindUserContext = {
  id?: string;
  roles?: { id: number; isInhouse?: boolean }[];
  selectedRole: number;
};

/**
 * UI default for "Which samples" on the Samples page. {@link defaultFilterCriteria} keeps `kind: Both`
 * as the API baseline; this picks Active for in-house roles and when "My samples" is checked.
 */
export function defaultSampleSearchKindForUser(
  user: SampleSearchKindUserContext | null | undefined,
  filters: Pick<SampleSearchCriteria, 'addedByUserId'>
): SampleSearchKind {
  const mySamples = !!user?.id && !!filters.addedByUserId && filters.addedByUserId === user.id;
  if (mySamples || (user && isInhouseUser(user))) {
    return SampleSearchKind.Active;
  }
  return SampleSearchKind.Both;
}

/** Samples-page default criteria with role-aware Kind applied. */
export function buildDefaultSampleSearchCriteriaForUser(
  user: SampleSearchKindUserContext | null | undefined,
  base: SampleSearchCriteria = defaultFilterCriteria
): SampleSearchCriteria {
  const kind = defaultSampleSearchKindForUser(user, base);
  return applySampleSearchKind({ ...base }, kind);
}

/**
 * POST body Kind for `/samples/search`: samples page uses the chosen Kind (default Both);
 * Substrates inventory page always sends Active (live pool — not a rule that substrates cannot be packaged).
 */
export function sampleGridSearchCriteria(
  filters: SampleSearchCriteria,
  listMode: SampleListMode
): SampleSearchCriteria {
  if (listMode === 'substrates') {
    return { ...filters, kind: SampleSearchKind.Active };
  }
  return applySampleSearchKind(filters, filters.kind ?? SampleSearchKind.Both);
}
